import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./global.css";
import { captureInstallPrompt } from "./lib/installPrompt";
import { initWebObservability } from "./lib/observability";
import { registerServiceWorker } from "./lib/sw";

// Errors-only Sentry, page-only (never the SW — see lib/observability.ts
// and docs/observability.md). Must run before the tree mounts so a crash
// during the very first render is still captured by the ErrorBoundary
// below.
initWebObservability();

// Capture the deferred `beforeinstallprompt` event BEFORE React
// mounts. Chromium fires it once, early in the page lifecycle —
// attaching the listener after `createRoot` would race the event
// and miss the first eligibility check on cold loads.
//
// …except in PR Fracture preview builds, which are served under a
// subpath like `/pr-123/`. The PWA manifest is hardcoded
// with `start_url: "/"` and `scope: "/"`, so installing from a
// preview would land users at the wrong URL (root, not the preview
// subpath). `VITE_DISABLE_INSTALL` gates both this capture and the
// `<InstallPrompt />` render in `MainPage.tsx`. Any non-empty value
// disables — string truthiness, so don't write `=false`.
if (!import.meta.env.VITE_DISABLE_INSTALL) {
  captureInstallPrompt();
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root not found");
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);

// Register the PWA service worker after scheduling the React render.
// `createRoot(...).render(...)` only schedules the work — the tree
// hasn't actually mounted by the time this line runs. That's still
// the right ordering: `registerServiceWorker` defers further inside
// to the `load` event so the SW network request never competes
// with first-paint bandwidth either way.
registerServiceWorker();
